import { useEffect } from 'react';
import Footer from '../../components/feature/Footer';

export default function AccessibilityPage() {
  useEffect(() => {
    updatePageSEO({
      title: 'Accessibility Statement | Curtis Waters AI Solutions',
      description: 'Accessibility commitment for Curtis Waters AI Solutions. WCAG 2.1 compliant AI platform. Learn about our accessibility features, assistive technology support, and inclusive design for deal evaluation and business intelligence.',
      keywords: 'accessibility, WCAG compliance, inclusive design, assistive technology, accessible AI, web accessibility, ADA compliance, accessible business intelligence',
      canonical: `${import.meta.env.VITE_SITE_URL}/accessibility`,
      ogTitle: 'Accessibility Statement | Curtis Waters AI Solutions',
      ogDescription: 'Accessibility commitment for Curtis Waters AI Solutions. WCAG 2.1 compliant AI platform. Learn about our accessibility features, assistive technology support, and inclusive design for deal evaluation and business intelligence.',
      ogUrl: `${import.meta.env.VITE_SITE_URL}/accessibility`,
    });
    
    const siteUrl = import.meta.env.VITE_SITE_URL || 'https://curtiswaters.ai';
    
    const schemaData = {
      "@context": "https://schema.org",
      "@type": "WebPage",
      "name": "Accessibility Statement - Curtis Waters AI",
      "description": "Accessibility Statement for Curtis Waters AI. Our commitment to making our website accessible to everyone.",
      "url": `${siteUrl}/accessibility`
    };

    const existingScript = document.querySelector('script[data-schema="accessibility"]');
    if (existingScript) {
      existingScript.remove();
    }

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.setAttribute('data-schema', 'accessibility');
    script.textContent = JSON.stringify(schemaData);
    document.head.appendChild(script);

    return () => {
      const schemaScript = document.querySelector('script[data-schema="accessibility"]');
      if (schemaScript) {
        schemaScript.remove();
      }
    };
  }, []);

  const revealEmail = () => {
    const user = 'curtis';
    const domain = 'curtiswaters';
    const tld = 'com';
    window.location.href = `mailto:${user}@${domain}.${tld}`;
  };

  return (
    <div className="min-h-screen bg-white">
      <header className="bg-gray-900 text-white py-6">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <a href="/" className="inline-flex items-center text-gray-300 hover:text-white mb-4 transition-colors cursor-pointer">
            <i className="ri-arrow-left-line mr-2"></i>
            Back to Home
          </a>
          <h1 className="text-3xl sm:text-4xl font-bold" style={{ fontFamily: '"Pacifico", serif' }}>
            Accessibility Statement
          </h1>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="prose prose-lg max-w-none">
          <p className="text-gray-600 mb-8">
            <strong>Last Updated:</strong> {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
          </p>

          <section className="mb-10">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">1. Our Commitment</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              Curtis Waters AI is committed to ensuring digital accessibility for people with disabilities. We are continually improving the user experience for everyone and applying the relevant accessibility standards to ensure we provide equal access to all of our users.
            </p>
            <p className="text-gray-700 leading-relaxed">
              We believe that everyone should be able to access our AI-powered business intelligence services, regardless of their abilities or the technology they use.
            </p>
          </section>

          <section className="mb-10">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">2. Conformance Status</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              The Web Content Accessibility Guidelines (WCAG) define requirements for designers and developers to improve accessibility for people with disabilities. It defines three levels of conformance: Level A, Level AA, and Level AAA.
            </p>
            <p className="text-gray-700 leading-relaxed">
              Curtis Waters AI is partially conformant with WCAG 2.1 level AA. Partially conformant means that some parts of the content do not fully conform to the accessibility standard, but we are actively working to achieve full conformance.
            </p>
          </section>

          <section className="mb-10">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">3. Accessibility Features</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              Our website includes the following accessibility features:
            </p>
            
            <h3 className="text-xl font-semibold text-gray-900 mb-3 mt-6">3.1 Navigation and Structure</h3>
            <ul className="list-disc pl-6 text-gray-700 space-y-2 mb-4">
              <li>Clear and consistent navigation throughout the site</li>
              <li>Logical heading structure (H1, H2, H3, etc.)</li>
              <li>Descriptive page titles</li>
              <li>Skip navigation links for keyboard users</li>
              <li>Breadcrumb navigation where appropriate</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-900 mb-3 mt-6">3.2 Visual Design</h3>
            <ul className="list-disc pl-6 text-gray-700 space-y-2 mb-4">
              <li>Sufficient color contrast between text and background</li>
              <li>Text that can be resized up to 200% without loss of functionality</li>
              <li>Responsive design that works on various screen sizes</li>
              <li>Information not conveyed by color alone</li>
              <li>Clear focus indicators for keyboard navigation</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-900 mb-3 mt-6">3.3 Content</h3>
            <ul className="list-disc pl-6 text-gray-700 space-y-2 mb-4">
              <li>Alternative text for images</li>
              <li>Descriptive link text</li>
              <li>Clear and simple language</li>
              <li>Properly labeled form fields</li>
              <li>Error messages that are clear and helpful</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-900 mb-3 mt-6">3.4 Keyboard Accessibility</h3>
            <ul className="list-disc pl-6 text-gray-700 space-y-2 mb-4">
              <li>All functionality available via keyboard</li>
              <li>Logical tab order</li>
              <li>No keyboard traps</li>
              <li>Visible focus indicators</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-900 mb-3 mt-6">3.5 Screen Reader Compatibility</h3>
            <ul className="list-disc pl-6 text-gray-700 space-y-2">
              <li>Semantic HTML markup</li>
              <li>ARIA labels and landmarks where appropriate</li>
              <li>Proper heading hierarchy</li>
              <li>Descriptive labels for interactive elements</li>
            </ul>
          </section>

          <section className="mb-10">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">4. Assistive Technologies</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              Our website is designed to be compatible with the following assistive technologies:
            </p>
            <ul className="list-disc pl-6 text-gray-700 space-y-2">
              <li>Screen readers (JAWS, NVDA, VoiceOver)</li>
              <li>Screen magnification software</li>
              <li>Speech recognition software</li>
              <li>Alternative input devices</li>
              <li>Browser text-to-speech extensions</li>
            </ul>
          </section>

          <section className="mb-10">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">5. Known Limitations</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              Despite our best efforts, some limitations may exist. Known accessibility issues include:
            </p>
            <ul className="list-disc pl-6 text-gray-700 space-y-2 mb-4">
              <li>Some third-party embedded content may not be fully accessible</li>
              <li>Certain complex interactive features may require additional keyboard commands</li>
              <li>Some PDF documents may not be fully accessible (we are working to remediate these)</li>
            </ul>
            <p className="text-gray-700 leading-relaxed">
              We are actively working to address these limitations and improve accessibility across our entire website.
            </p>
          </section>

          <section className="mb-10">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">6. Browser and Device Compatibility</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              Our website is designed to work with the following browsers and devices:
            </p>
            
            <h3 className="text-xl font-semibold text-gray-900 mb-3 mt-6">6.1 Supported Browsers</h3>
            <ul className="list-disc pl-6 text-gray-700 space-y-2 mb-4">
              <li>Google Chrome (latest version)</li>
              <li>Mozilla Firefox (latest version)</li>
              <li>Safari (latest version)</li>
              <li>Microsoft Edge (latest version)</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-900 mb-3 mt-6">6.2 Supported Devices</h3>
            <ul className="list-disc pl-6 text-gray-700 space-y-2">
              <li>Desktop computers (Windows, macOS, Linux)</li>
              <li>Tablets (iOS, Android)</li>
              <li>Smartphones (iOS, Android)</li>
            </ul>
          </section>

          <section className="mb-10">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">7. Third-Party Content</h2>
            <p className="text-gray-700 leading-relaxed">
              Some content on our website may be provided by third parties (e.g., embedded videos, social media feeds). While we strive to ensure all content is accessible, we cannot always control the accessibility of third-party content. We encourage third-party content providers to make their content accessible.
            </p>
          </section>

          <section className="mb-10">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">8. Ongoing Efforts</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              We are committed to continuously improving accessibility. Our ongoing efforts include:
            </p>
            <ul className="list-disc pl-6 text-gray-700 space-y-2">
              <li>Regular accessibility audits and testing</li>
              <li>Training our team on accessibility best practices</li>
              <li>Incorporating accessibility into our design and development process</li>
              <li>Testing with assistive technologies and real users</li>
              <li>Staying current with accessibility standards and guidelines</li>
              <li>Addressing reported accessibility issues promptly</li>
            </ul>
          </section>

          <section className="mb-12">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Contact Information</h2>
            <div className="bg-gray-50 p-6 rounded-lg">
              <p className="text-gray-700 mb-4">
                If you have questions, concerns, or feedback about the accessibility of our website, please contact us:
              </p>
              <div className="space-y-2">
                <p className="text-gray-700 mb-2"><strong>Curtis Waters AI</strong></p>
                <p className="text-gray-700 mb-2">
                  Email: <button 
                    onClick={revealEmail}
                    className="text-red-600 hover:text-red-700 underline cursor-pointer bg-transparent border-none p-0 font-inherit"
                  >
                    Click to reveal email
                  </button>
                </p>
                <p className="text-gray-700 mb-2">Subject Line: "Accessibility Feedback"</p>
              </div>
            </div>
          </section>

          <section className="mb-12">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Alternative Formats</h2>
            <div className="bg-gray-50 p-6 rounded-lg">
              <p className="text-gray-700 leading-relaxed">
                If you require information from our website in an alternative format (e.g., large print, audio, braille), please contact us using the button above. We will work with you to provide the information in a format that meets your needs.
              </p>
            </div>
          </section>

          <section className="mb-12">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Complaint Process</h2>
            <div className="bg-gray-50 p-6 rounded-lg">
              <p className="text-gray-700 mb-4">
                If you believe our website does not meet accessibility standards, you may file a complaint:
              </p>
              <ol className="list-decimal pl-6 text-gray-700 space-y-2">
                <li>Contact us using the email button above with "Accessibility Complaint" in the subject line</li>
                <li>Provide details about the accessibility issue and any previous correspondence</li>
                <li>We will acknowledge receipt within 2 business days</li>
                <li>We will investigate and respond with our findings within 10 business days</li>
              </ol>
            </div>
          </section>

          <section className="mb-10">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">12. Technical Specifications</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              Accessibility of Curtis Waters AI relies on the following technologies:
            </p>
            <ul className="list-disc pl-6 text-gray-700 space-y-2">
              <li>HTML5</li>
              <li>CSS3</li>
              <li>JavaScript</li>
              <li>WAI-ARIA</li>
            </ul>
          </section>

          <section className="mb-10">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">13. Assessment Approach</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              Curtis Waters AI assessed the accessibility of our website using the following methods:
            </p>
            <ul className="list-disc pl-6 text-gray-700 space-y-2">
              <li>Self-evaluation using automated accessibility testing tools</li>
              <li>Manual testing with keyboard navigation</li>
              <li>Testing with screen readers</li>
              <li>Review of code for semantic HTML and ARIA implementation</li>
              <li>Color contrast analysis</li>
            </ul>
          </section>

          <section className="mb-10">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">14. Updates to This Statement</h2>
            <p className="text-gray-700 leading-relaxed">
              This accessibility statement was last reviewed and updated on {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}. We will continue to review and update this statement as we make improvements to our website's accessibility.
            </p>
          </section>
        </div>
      </main>

      <Footer />
    </div>
  );
}